<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            Adding a virtual tour to a property listing is one of the
            highest-impact actions an agent can take — but only if it's done
            correctly. A tour buried in an unreadable URL or added to the wrong
            field gets ignored. This step-by-step guide covers every major
            platform and method for adding a virtual tour to your listing,
            including MLS, Zillow, Realtor.com, and your own website, so buyers
            see it.
          </p>
        </section>

        <section class="key-takeaways">
          <h2>Key Takeaways</h2>
          <ul>
            <li>Most MLS platforms now accept virtual tour URLs in the 'Virtual Tour' or 'Media' field.</li>
            <li>Zillow, Realtor.com, and Redfin all display virtual tour links prominently on listings.</li>
            <li>Use a branded, trackable URL for each tour to measure viewer engagement.</li>
            <li>Embed tours on your personal website for SEO value and lead capture.</li>
            <li>Always test tour links across desktop, mobile, and tablet before publishing.</li>
          </ul>
        </section>

        <section>
          <h2>Step 1: Create Your Virtual Tour</h2>

          <h3>Choose Your Capture Method</h3>
          <p>
            Before you can add a tour to your listing, you need one. The fastest
            approach for most agents is using Travvir or a similar smartphone
            360 app to create the tour yourself during the property walkthrough
            or staging visit. For a complete capture guide, see
            <a href="/blog/en/360-real-estate-photography-guide"
              >360 real estate photography: complete guide for agents</a
            >.
          </p>

          <h3>Get Your Tour URL</h3>
          <p>
            Once your tour is processed and published, your hosting platform
            provides a shareable link. This is the URL you'll add to every
            listing platform. In Travvir, find this under the tour's "Share"
            option. The URL should look like a standard web address — not a file
            path or download link. Test the URL in a private browser window to
            confirm it loads the tour correctly before adding it to your
            listing.
          </p>
        </section>

        <section>
          <h2>Adding a Virtual Tour to MLS Listings</h2>

          <h3>The Standard MLS Virtual Tour URL Field</h3>
          <p>
            Most MLS systems include a dedicated "Virtual Tour" or "Tour URL"
            field in the listing editor. This is separate from the remarks or
            notes fields, and it's where your tour link belongs. The field
            accepts a standard HTTPS URL — paste your tour link directly here.
            <strong>MLS rules vary</strong> by region: some allow any virtual
            tour URL, while others restrict it to specific platform formats.
            Check your MLS's media guidelines before publishing.
          </p>

          <h3>RETS and IDX Distribution</h3>
          <p>
            When you add a virtual tour URL to MLS, it's distributed via RETS or
            RESO data feeds to all IDX-connected websites — including your
            brokerage's agent pages, third-party property portals synced to your
            MLS, and the buyer portals your office uses. This means
            <strong
              >a single MLS entry pushes your tour to dozens of platforms
              automatically</strong
            >.
          </p>

          <h3>Common MLS-Specific Instructions</h3>
          <p>
            <strong>CRMLS (California):</strong> Enter the tour URL in the
            "Virtual Tour 1" field under Media. CRMLS supports multiple tour
            URLs (Virtual Tour 1 and 2) — use the first for your primary tour
            and the second for video walkthrough or Zillow 3D.
            <strong>BRIGHT MLS (Mid-Atlantic):</strong> Tour URL field is in the
            Media Upload section under "Virtual Tour." Supports any HTTPS link.
            <strong>NTREIS (North Texas):</strong> Virtual Tour URL field in
            listing Input. Important: NTREIS requires the URL to display the
            tour directly, not redirect to a login page.
            <strong>FMLS (Georgia):</strong> Virtual Tour URL in the
            Features/Details section. URL must be accessible without a password.
          </p>
          <p>
            If you're unsure where to find the field in your specific MLS,
            search your MLS help documentation for "virtual tour URL" or contact
            your MLS support line.
          </p>
        </section>

        <section>
          <h2>Adding a Virtual Tour to Zillow</h2>

          <h3>Method 1: Through Zillow 3D Home App</h3>
          <p>
            The most direct Zillow integration is through Zillow's own app.
            Create a Zillow 3D Home tour (as described in our
            <a href="/blog/en/zillow-3d-tour-vs-360-virtual-tour"
              >Zillow 3D vs 360 tour comparison</a
            >), which automatically adds the 3D Tour badge to your Zillow
            listing.
          </p>

          <h3>Method 2: Manual URL Addition via Zillow Premier Agent</h3>
          <p>
            If you have a non-Zillow tour (Travvir, Matterport, etc.) that you
            want featured on Zillow, you can add the URL manually: Log into your
            Zillow Premier Agent account → Find the listing → Select "Edit
            listing" → Scroll to the Media section → Add your virtual tour URL
            in the designated field. Note: Third-party tour URLs on Zillow do
            not receive the "3D Tour" badge, but they do appear as a clickable
            "Virtual Tour" link on the listing page.
          </p>
          <p>
            <strong>The hybrid strategy</strong> many agents use: create a
            Zillow 3D Home tour to get the badge, plus add a Travvir tour URL
            for full-quality viewing and embedding elsewhere.
          </p>
        </section>

        <section>
          <h2>Adding a Virtual Tour to Realtor.com</h2>

          <h3>Via the Listing Agent Portal</h3>
          <p>
            Log into your Realtor.com listing management portal. Select the
            listing → Media. In the Virtual Tour section, paste your tour URL.
            Realtor.com accepts any standard HTTPS virtual tour link. The tour
            appears as a "Virtual Tour" button on the listing details page.
            <strong
              >Realtor.com distributes to Move.com, Homes.com, and affiliate
              portals</strong
            >
            that use their data feed.
          </p>

          <h3>Via MLS Distribution</h3>
          <p>
            In most cases, your Realtor.com listing is automatically populated
            from your MLS data feed. If you've already added the tour URL to
            your MLS listing correctly, it should sync to Realtor.com
            automatically within 24–48 hours. If it doesn't, use the manual
            portal method above.
          </p>
        </section>

        <section>
          <h2>Adding a Virtual Tour to Your Agency Website</h2>

          <h3>Using an Embed Code (Iframe)</h3>
          <p>
            Most virtual tour platforms provide an embed code — a small snippet
            of HTML that you paste into your website's page editor. This embeds
            the interactive tour directly on your property page so visitors can
            explore it without leaving your site. In Travvir, find the embed
            code under the tour's "Share" → "Embed."
          </p>
          <p>
            The embed code looks like this example structure:
            <code
              >&lt;iframe src="[your-tour-url]" width="100%" height="600"
              frameborder="0" allowfullscreen&gt;&lt;/iframe&gt;</code
            >
          </p>
          <p>
            For WordPress sites, paste this in the HTML editor block. For
            Squarespace, use an Embed block. For Wix, use the HTML embed
            element.
          </p>

          <h3>Responsive Sizing</h3>
          <p>
            Set the iframe width to 100% (not a fixed pixel width) and height to
            at least 500px for mobile-friendly display. On mobile devices, a
            100% width iframe with 500px height allows effective 360 navigation.
            Test your embedding on both desktop and mobile before the listing
            goes live.
          </p>
        </section>

        <section>
          <h2>Adding Virtual Tours to Email Marketing</h2>

          <h3>The Right Way to Include Tours in Listing Emails</h3>
          <p>
            Email clients cannot render iframes or interactive content — never
            try to embed an actual 360 tour in an email. Instead, include a
            screenshot or thumbnail image of the tour that links to the hosted
            tour URL when clicked. Include a clear call-to-action such as "Click
            to Take the Virtual Tour" as both a button and text link.
          </p>
          <p>
            Tour links in email marketing achieve
            <strong>2.8× higher click-through rates</strong> than standard photo
            gallery links in real estate listing emails. The word "tour" in a
            subject line also improves open rates by approximately
            <strong>18%</strong>
            compared to standard "just listed" email subject lines.
          </p>
        </section>

        <section>
          <h2>Adding Tours to Social Media Posts</h2>

          <h3>Facebook and Instagram</h3>
          <p>
            Facebook supports 360 photo posts natively — if your tour platform
            allows downloading individual equirectangular JPEG files, you can
            upload them directly to Facebook for an interactive experience. For
            Instagram, share a screengrab or walkthrough video clip, and include
            your tour link in the caption or bio link. See our guide on
            <a href="/blog/en/share-360-photo-social-media"
              >sharing 360 photos on social media</a
            >
            for platform-specific tips.
          </p>
        </section>

        <section>
          <h2>Troubleshooting Common Issues</h2>

          <h3>Tour URL Not Accepted by MLS</h3>
          <p>
            Some MLS systems reject URLs containing redirect chains, tracking
            parameters, or that require cookies to load. Try shortening your URL
            using Bitly or your platform's built-in link shortener. Also ensure
            the URL uses HTTPS (not HTTP). If the issue persists, contact your
            MLS support — many have specific whitelist procedures for new tour
            platforms.
          </p>

          <h3>Tour Not Showing on Third-Party Portals</h3>
          <p>
            MLS data feeds to portals like Zillow, Realtor.com, and Homes.com
            can take 24–48 hours to sync. If your tour doesn't appear after 48
            hours, log into each portal's listing management tool directly and
            add the tour URL manually using the portal-specific instructions
            above.
          </p>
        </section>

        <section>
          <h2>Conclusion: Getting Your Tour Seen</h2>
          <p>
            Adding a virtual tour to your property listing requires a few extra
            minutes but delivers outsized results. The key is
            <strong>adding your tour URL to every available platform</strong> —
            MLS, Zillow, Realtor.com, your agency website, and your email
            campaigns — so it reaches buyers regardless of where they search.
          </p>
          <p>
            One tour URL, properly distributed, can reach every buyer searching
            on every major platform simultaneously. That's the most efficient
            marketing lever available to real estate agents today.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
          <li><a href="/blog/en/how-to-make-virtual-tour-real-estate">How to Make a Real Estate Tour</a></li>
          <li><a href="/blog/en/embed-360-virtual-tour-website">Embed a Tour on Your Website</a></li>
          <li><a href="/blog/en/virtual-tour-seo-guide">Virtual Tour SEO Guide</a></li>
          <li><a href="/blog/en/realtor-virtual-tour-2026">Why Realtors Need Tours in 2026</a></li>
          <li><a href="/blog/en/best-virtual-tour-software-real-estate">Best RE Software</a></li>
          <li><a href="/blog/en/virtual-tours-vs-property-photos">Tours vs Photos</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "add-virtual-tour-to-listing",
);

useBlogSeo({
  title: "Add a Virtual Tour to MLS & Property Listings [2026] | Travvir",
  description:
    "How to add a virtual tour to your MLS listing, Zillow, Realtor.com, and other property portals. Covers embed links, branded URLs, and syndication tips.",
  keywords:
    "add virtual tour to listing, mls virtual tour, how to add virtual tour mls, zillow virtual tour url, realtor virtual tour listing",
  slug: "add-virtual-tour-to-listing",
  image: postData?.image,
  postData,
  lang: "en",
});

</script>
